import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import { List, Set } from "immutable";
import React, { useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useCourseWorkspaces } from "@domain/group/workspace/hooks";
import { COURSE_VERSION } from "@domain/lms/course/course";
import { CourseDraft } from "@domain/lms/course/courseDraft";
import { CourseSection, LoadingSection } from "@domain/lms/course/courseSection";
import { ExerciseDraft } from "@domain/lms/course/exercise/exerciseDraft";
import { useDetailedCourse } from "@domain/lms/course/hooks";
import { withId } from "@utils/domain";
import { Page } from "@ui/components/page";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { CourseEditor } from "./courseEditor";

interface CourseEditorPageProps extends React.HTMLAttributes<HTMLDivElement> {
	courseId?: string;
	workspaceId?: string;
}
export const CourseEditorPage: React.FC<CourseEditorPageProps> = ({ courseId, workspaceId, ...htmlProps }) =>
	courseId ? (
		<CourseUpdatePage courseId={courseId} {...htmlProps} />
	) : (
		<CourseCreationPage workspaceId={workspaceId} {...htmlProps} />
	);

interface CourseUpdatePageProps extends CourseEditorPageProps {
	courseId: string;
}
const CourseUpdatePage: React.FC<CourseUpdatePageProps> = ({ courseId, ...htmlProps }) => {
	const { result: initialCourse, loading, error } = useDetailedCourse(courseId);
	const { result: workspaces, loading: workspacesLoading } = useCourseWorkspaces(courseId);
	const [courseEditionStep, setCourseEditionStep] = useState<"MAIN" | "DETAILS">("MAIN");
	const history = useHistory();

	if (error?.code === 404) {
		return <NotFoundPage message={I18n.t("404.course")} />;
	}

	return (
		<Page
			{...htmlProps}
			backAction={courseEditionStep === "DETAILS" ? () => setCourseEditionStep("MAIN") : () => history.goBack()}
			backText={courseEditionStep === "DETAILS" ? I18n.t("course.editor.step.back") : undefined}
			header={courseEditionStep === "DETAILS" ? undefined : I18n.t("course.editor.header")}
		>
			{loading || workspacesLoading ? (
				<ClipLoader />
			) : (
				initialCourse && (
					<CourseEditor
						step={courseEditionStep}
						onStepChange={setCourseEditionStep}
						initialCourse={{ ...initialCourse, workspaceIds: Set(workspaces.map(({ id }) => id)) }}
						courseId={courseId}
					/>
				)
			)}
		</Page>
	);
};

interface CourseCreatePageProps extends CourseEditorPageProps {
	workspaceId?: string;
}
const CourseCreationPage: React.FC<CourseCreatePageProps> = ({ workspaceId, ...htmlProps }) => {
	const currentProfile = useCurrentProfile();
	const [courseEditionStep, setCourseEditionStep] = useState<"MAIN" | "DETAILS">("MAIN");
	const history = useHistory();

	const initialCourse: CourseDraft = {
		title: "",
		description: ContentState.createFromText(""),
		duration: 0,
		status: "DRAFT",
		language: currentProfile?.language ?? "ENGLISH",
		content: {
			version: COURSE_VERSION,
			chapters: List([
				withId({
					title: "",
					sections: List<CourseSection | LoadingSection>(),
					exercises: List<ExerciseDraft>(),
				}),
			]),
			assets: List(),
		},
		workspaceIds: Set(workspaceId ? [workspaceId] : []),
		visibility: "PUBLIC",
		keepOwnership: true,
		contentType: "COURSE",
	};

	return (
		<Page
			{...htmlProps}
			backAction={courseEditionStep === "DETAILS" ? () => setCourseEditionStep("MAIN") : () => history.goBack()}
			backText={courseEditionStep === "DETAILS" ? I18n.t("course.editor.step.back") : undefined}
			header={courseEditionStep === "DETAILS" ? undefined : I18n.t("course.editor.new.header")}
		>
			<CourseEditor initialCourse={initialCourse} step={courseEditionStep} onStepChange={setCourseEditionStep} />
		</Page>
	);
};
